import React, { createContext, useContext, useEffect, useState } from 'react';
import useAuthStore from '../store/authStore';
import { getCurrentUser } from '../services/api/auth';
import type { User } from '../types';
import { config } from '../config';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  deductCredits: (amount: number) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const { 
    user, 
    isAuthenticated, 
    isLoading,
    login, 
    logout, 
    deductCredits,
    setLoading 
  } = useAuthStore();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('auth-token');
        
        if (!token) {
          setLoading(false);
          setInitialized(true);
          return;
        }

        console.log('Initializing auth with API URL:', config.apiUrl);

        // Get current user data
        try {
          const { success, user } = await getCurrentUser();
          if (success && user) {
            login(token, user);
          } else {
            throw new Error('Failed to get user data');
          }
        } catch (error) {
          console.error('Failed to get user data:', error);
          localStorage.removeItem('auth-token');
          logout();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('auth-token');
        logout();
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    if (!initialized) {
      initializeAuth();
    }
  }, [initialized, login, logout, setLoading]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      isAdmin: user?.role === 'admin',
      login,
      logout,
      deductCredits
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};